import { InputBaseProps, styled, TextFieldProps } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";
import { InputBase } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { GridCellParams } from "@mui/x-data-grid";
import { DateOrTimeView } from "@mui/x-date-pickers/models";
import { useEffect, useState } from "react";
import { StyledTooltip } from "./buyerListingGridEditText";
import { INVALID_DATE, INVALID_FUTURE_DATE } from "../../../utils/common/validator";

export const GridEditDateTimeInput = styled(InputBase)({
  fontSize: "inherit",
  padding: "0 9px",
});

export function WrappedGridEditDateTimeInput(props: TextFieldProps) {
  const { InputProps, focused, ...other } = props;
  return (
    <GridEditDateTimeInput
      fullWidth
      {...InputProps}
      {...(other as InputBaseProps)}
    />
  );
}

interface GridEditDateTimeCellProps {
  id: string;
  field: string;
  value: Dayjs | null;
  params: GridCellParams;
  handleDateTime: (
    id: string,
    field: string,
    value: Dayjs | null,
    params: GridCellParams
  ) => Promise<void>;
}

export function GridEditDateTimeCell({
  id,
  field,
  value,
  params,
  handleDateTime,
}: GridEditDateTimeCellProps) {
  const apiRef = useGridApiContext();
  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState<Dayjs | null>(null);
  const [showError, setShowError] = useState(false);
  const [showText, setShowText] = useState("");
  const [ error, setError] = useState<boolean>(false);
  const ERROR_DISPLAY_DURATION = 5000;

  useEffect(() => {
    if (showError) {
      const timer = setTimeout(
        () => setShowError(false),
        ERROR_DISPLAY_DURATION
      );
      return () => clearTimeout(timer);
    }
  }, [showError]);
  
  const handleOpen = () => {
    setOpen(true);
  }

  const handleAccept =  async (nValue: Dayjs | null)=> {
    const updateValue = nValue ? (dayjs.isDayjs(nValue) ? nValue : dayjs(nValue)) : null;
    if (updateValue && !updateValue.isValid()) {
      handleOnCancel();
      return;
    } 
    setResult(updateValue ? updateValue.format("YYYY-MM-DD HH:mm") : "");
    try {
       await handleDateTime(id, field, updateValue, params);
    } catch (error) {
      console.error("Failed to handle date change:", error);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const pickerViews: readonly DateOrTimeView[] = [
    "year",
    "month",
    "day",
    "hours",
    "minutes",
  ];

  function setCellValue(v: string) {
    apiRef.current.setEditCellValue({
      id,
      field,
      value: v,
    });
  }

  function setResult(v: string) {
    setCellValue(v);
    apiRef.current.stopCellEditMode({
      id,
      field,
    });
  }


  function handleOnCancel() {
    const updateValue = value ? dayjs(value).format("MM-DD-YYYY HH:mm") : "";
    setResult(updateValue);
  }

  return (
    <StyledTooltip open={showError} title={showText}>
      <div>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
          <DateTimePicker
            views={pickerViews}
            value={value && dayjs(value).isValid() ? dayjs(value) : null}
            open={open}
            onOpen={handleOpen}
            onChange={(newValue) => {
              setNewValue(newValue);
            }}
            autoFocus
            onAccept={async (nValue: Dayjs | null) => {
              handleAccept(nValue);
            }}
            onClose={() => {
              handleClose();
            }} 
            onError={(error) => {
              switch (error) {
              case "invalidDate":
                setShowError(true);
                setShowText(INVALID_DATE);
                setError(true);
                break;
              case "disableFuture":
                setShowError(true);
                setShowText(INVALID_FUTURE_DATE);
                setError(true);
                break;
              case null:
                setShowError(false);
                setShowText("");
                setError(false);
                break;
              }
            }}
            slots={{ textField: WrappedGridEditDateTimeInput }}
            format="MM-DD-YYYY HH:mm"
            ampm={false}
            timeSteps={{ minutes: 15 }}
            slotProps={{
              popper: {
                onBlur: (event: React.FocusEvent<HTMLDivElement>) => {
                  if (event.relatedTarget && event.relatedTarget.tagName === "DIV") {
                    if (newValue && dayjs(newValue).isValid()) {
                      handleAccept(newValue);
                    } else {
                      handleAccept(value);
                    }
                  }
                }
              },
              textField: {
                inputProps: { spellCheck: false },
                onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                  const raw = event.target.value.trim();
                  if (event.relatedTarget?.tagName !== "DIV") {
                    return
                  }
                  if (raw === "MM-DD-YYYY hh:mm" || raw === "") {
                    // setCellValue("");
                    handleAccept(null);
                    return
                  }
                  const eValue = dayjs(raw);
                  if (eValue.isValid() && !error) {
                    // setCellValue(eValue.format("MM-DD-YYYY HH:mm"));
                    handleAccept(eValue);
                  } else {
                    // setCellValue(value ? value.format("MM-DD-YYYY HH:mm") : "");
                    handleAccept(dayjs(value));
                  }
                },
                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                  const raw = (event.target as HTMLInputElement).value.trim();
                  if (event.key === "Enter" || event.key === "Tab") {
                    if (raw === "MM-DD-YYYY hh:mm" || raw === "") {
                      // setCellValue("");
                      handleAccept(null)
                      return;
                    }
                    const eValue = dayjs(raw);
                    if (eValue.isValid() && !error) {
                      // setCellValue(eValue.format("MM-DD-YYYY"));
                      handleAccept(eValue);
                    } else {
                      setCellValue(dayjs(value)?.format("MM-DD-YYYY HH:mm") || "");
                    }
                  } else if (event.key === "Escape") {
                    handleOnCancel();
                  }
                },
              },
              actionBar: { actions: ["clear", "accept"],
                onClear: () => {
                  setResult("");
                  handleDateTime(id, field, null, params);
                  handleClose();
                },
              },
            }}
            closeOnSelect={false}
          />
        </LocalizationProvider>
      </div>
    </StyledTooltip>
  );
}
